import { useMemo, useState, useCallback } from 'react';
import {
  Text,
  Paper,
  Stack,
  Group,
  Table,
  Button,
  Skeleton,
  SimpleGrid,
  Drawer,
  Divider,
  ActionIcon,
  Tooltip,
  Pagination,
  Modal,
  TextInput,
  NumberInput,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconChartBar,
  IconChevronUp,
  IconChevronDown,
  IconSelector,
  IconEye,
  IconPlus,
} from '@tabler/icons-react';
import PageHeader from '@/components/PageHeader';
import StatusBadge from '@/components/StatusBadge';
import StatCard from '@/components/StatCard';
import EmptyState from '@/components/EmptyState';
import {
  useBudgets,
  useCreateBudget,
  useBudgetVariance,
} from '@/hooks/usePortalApi';
import type { BudgetItem } from '@/types';

type SortField = 'name' | 'fiscal_year' | 'status';
type SortDir = 'asc' | 'desc';
const PAGE_SIZE = 20;

function SortIcon({ field, current, dir }: { field: SortField; current: SortField | null; dir: SortDir }) {
  if (current !== field) return <IconSelector size={14} style={{ opacity: 0.4 }} />;
  return dir === 'asc' ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />;
}

function formatCurrency(val: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);
}

export default function Budgets() {
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState<SortField | null>('fiscal_year');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const [createOpened, { open: openCreate, close: closeCreate }] = useDisclosure(false);
  const [varianceOpened, { open: openVariance, close: closeVariance }] = useDisclosure(false);
  const [varianceBudgetId, setVarianceBudgetId] = useState(0);
  const [drawerBudget, setDrawerBudget] = useState<BudgetItem | null>(null);
  const [drawerOpen, { open: openDrawer, close: closeDrawer }] = useDisclosure(false);

  const [newName, setNewName] = useState('');
  const [newYear, setNewYear] = useState(new Date().getFullYear());

  const { data, isLoading } = useBudgets();
  const createMutation = useCreateBudget();
  const { data: varianceData, isLoading: varianceLoading } = useBudgetVariance(varianceBudgetId);

  const allBudgets = data?.items ?? [];

  const sorted = useMemo(() => {
    if (!sortField) return allBudgets;
    return [...allBudgets].sort((a, b) => {
      let cmp = 0;
      if (sortField === 'name') cmp = a.name.localeCompare(b.name);
      else if (sortField === 'fiscal_year') cmp = a.fiscal_year - b.fiscal_year;
      else cmp = a.status.localeCompare(b.status);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [allBudgets, sortField, sortDir]);

  const totalPages = Math.ceil(sorted.length / PAGE_SIZE);
  const budgets = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const activeCount = allBudgets.filter((b) => b.status === 'active').length;

  const toggleSort = (field: SortField) => {
    if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('desc'); }
    setPage(1);
  };

  const handleViewDrawer = useCallback(
    (budget: BudgetItem) => { setDrawerBudget(budget); openDrawer(); },
    [openDrawer],
  );

  const handleViewVariance = (id: number) => {
    setVarianceBudgetId(id);
    openVariance();
  };

  const handleCreate = () => {
    createMutation.mutate(
      { name: newName, fiscal_year: newYear },
      {
        onSuccess: () => { notifications.show({ title: 'Created', message: 'Budget created', color: 'green' }); closeCreate(); setNewName(''); },
        onError: () => notifications.show({ title: 'Error', message: 'Failed to create budget', color: 'red' }),
      },
    );
  };

  const ThSort = ({ field, children }: { field: SortField; children: React.ReactNode }) => (
    <Table.Th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort(field)}>
      <Group gap={4} wrap="nowrap">{children}<SortIcon field={field} current={sortField} dir={sortDir} /></Group>
    </Table.Th>
  );

  return (
    <Stack gap="lg">
      <PageHeader
        title="Budgets"
        description="Budget management and variance analysis"
        actions={<Button leftSection={<IconPlus size={16} />} onClick={openCreate}>New Budget</Button>}
      />

      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        <StatCard title="Active Budgets" value={String(activeCount)} icon={<IconChartBar size={24} />} />
        <StatCard title="Total Budgets" value={String(data?.total ?? 0)} icon={<IconChartBar size={24} />} />
      </SimpleGrid>

      {isLoading ? (
        <Stack gap="xs">{[...Array(5)].map((_, i) => <Skeleton key={i} height={50} radius="md" />)}</Stack>
      ) : budgets.length === 0 ? (
        <EmptyState icon={<IconChartBar size={30} />} title="No budgets" description="No budgets have been created yet" />
      ) : (
        <>
          <Paper>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={60}>ID</Table.Th>
                  <ThSort field="name">Budget Name</ThSort>
                  <ThSort field="fiscal_year">Fiscal Year</ThSort>
                  <ThSort field="status">Status</ThSort>
                  <Table.Th w={120} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {budgets.map((budget) => (
                  <Table.Tr key={budget.id}>
                    <Table.Td><Text size="sm" style={{ fontFamily: 'monospace' }}>{budget.id}</Text></Table.Td>
                    <Table.Td><Text size="sm" fw={500}>{budget.name}</Text></Table.Td>
                    <Table.Td><Text size="sm">{budget.fiscal_year}</Text></Table.Td>
                    <Table.Td><StatusBadge status={budget.status} /></Table.Td>
                    <Table.Td>
                      <Group gap={4} wrap="nowrap">
                        <Tooltip label="View details"><ActionIcon variant="subtle" size="sm" onClick={() => handleViewDrawer(budget)}><IconEye size={16} /></ActionIcon></Tooltip>
                        <Tooltip label="Variance analysis">
                          <Button size="xs" variant="light" onClick={() => handleViewVariance(budget.id)}>Variance</Button>
                        </Tooltip>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
          {totalPages > 1 && <Group justify="center"><Pagination total={totalPages} value={page} onChange={setPage} /></Group>}
        </>
      )}

      {/* Detail Drawer */}
      <Drawer opened={drawerOpen} onClose={closeDrawer} position="right" size="lg" title={<Text fw={700} size="lg">{drawerBudget?.name ?? 'Budget Details'}</Text>}>
        {drawerBudget ? (
          <Stack gap="md">
            <StatusBadge status={drawerBudget.status} />
            <SimpleGrid cols={2}>
              <div><Text size="xs" c="dimmed" tt="uppercase" fw={700}>Fiscal Year</Text><Text size="sm" fw={500}>{drawerBudget.fiscal_year}</Text></div>
              <div><Text size="xs" c="dimmed" tt="uppercase" fw={700}>Status</Text><Text size="sm" fw={500}>{drawerBudget.status}</Text></div>
            </SimpleGrid>
            <Divider />
            <Button variant="light" onClick={() => handleViewVariance(drawerBudget.id)}>View Variance Analysis</Button>
          </Stack>
        ) : (
          <Stack gap="xs">{[...Array(4)].map((_, i) => <Skeleton key={i} height={30} radius="md" />)}</Stack>
        )}
      </Drawer>

      {/* Create Modal */}
      <Modal opened={createOpened} onClose={closeCreate} title={<Text fw={600}>New Budget</Text>}>
        <Stack gap="md">
          <TextInput label="Budget Name" required value={newName} onChange={(e) => setNewName(e.currentTarget.value)} />
          <NumberInput label="Fiscal Year" value={newYear} onChange={(v) => setNewYear(Number(v) || new Date().getFullYear())} min={2020} max={2030} />
          <Button onClick={handleCreate} loading={createMutation.isPending} disabled={!newName} fullWidth>Create Budget</Button>
        </Stack>
      </Modal>

      {/* Variance Modal */}
      <Modal opened={varianceOpened} onClose={closeVariance} title={<Text fw={600}>Budget Variance Analysis</Text>} size="lg">
        {varianceLoading ? (
          <Stack gap="xs">{[...Array(5)].map((_, i) => <Skeleton key={i} height={40} radius="md" />)}</Stack>
        ) : varianceData?.items && varianceData.items.length > 0 ? (
          <Table striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Account</Table.Th>
                <Table.Th ta="right">Budgeted</Table.Th>
                <Table.Th ta="right">Actual</Table.Th>
                <Table.Th ta="right">Variance</Table.Th>
                <Table.Th ta="right">Variance %</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {varianceData.items.map((v) => (
                <Table.Tr key={v.account_id}>
                  <Table.Td><Text size="sm" fw={500}>{v.account_name}</Text></Table.Td>
                  <Table.Td ta="right"><Text size="sm">{formatCurrency(v.budgeted)}</Text></Table.Td>
                  <Table.Td ta="right"><Text size="sm">{formatCurrency(v.actual)}</Text></Table.Td>
                  <Table.Td ta="right"><Text size="sm" fw={500} c={v.variance >= 0 ? 'green' : 'red'}>{formatCurrency(v.variance)}</Text></Table.Td>
                  <Table.Td ta="right"><Text size="sm" c={v.variance_pct >= 0 ? 'green' : 'red'}>{v.variance_pct.toFixed(1)}%</Text></Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        ) : (
          <Text c="dimmed" ta="center" py="md">No variance data available</Text>
        )}
      </Modal>
    </Stack>
  );
}
